import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, Area, AreaChart } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ORIENT_COLORS = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444" };
const TYPE_COLORS = { legislation: "#ef4444", strategy: "#3b82f6", guideline: "#a855f7", standard: "#10b981", program: "#f59e0b", "governance-body": "#06b6d4" };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0c1222] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="font-bold text-white mb-1">{label}</p>
      {payload.map((p) => <p key={p.name || p.dataKey} style={{ color: p.color || p.stroke || "#94a3b8" }}>{p.name}: {p.value}</p>)}
    </div>
  );
};

export const PolicyTimeline = () => {
  const [policies, setPolicies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/policy/stats`),
      axios.get(`${API}/policy/list?limit=500&sort_by=year_adopted&order=asc`),
    ]).then(([s, p]) => {
      if (s.status === "fulfilled") setStats(s.value.data);
      if (p.status === "fulfilled") setPolicies(p.value.data.policies || []);
    }).finally(() => setLoading(false));
  }, []);

  // Cumulative timeline
  const cumulativeData = useMemo(() => {
    if (!stats?.timeline) return [];
    let total = 0;
    return stats.timeline.map(t => { total += t.count; return { year: t.year, count: t.count, cumulative: total }; });
  }, [stats]);

  // Orientation over time
  const orientTimeline = useMemo(() => {
    const years = {};
    policies.forEach(p => {
      const y = p.year_adopted;
      if (!years[y]) years[y] = { year: y, care: 0, utility: 0, creative: 0, extraction: 0 };
      years[y][p.value_orientation] = (years[y][p.value_orientation] || 0) + 1;
    });
    return Object.values(years).sort((a, b) => a.year - b.year);
  }, [policies]);

  // Type over time
  const typeTimeline = useMemo(() => {
    const years = {};
    policies.forEach(p => {
      const y = p.year_adopted;
      if (!years[y]) years[y] = { year: y };
      years[y][p.policy_type] = (years[y][p.policy_type] || 0) + 1;
    });
    return Object.values(years).sort((a, b) => a.year - b.year);
  }, [policies]);

  // Key milestones
  const milestones = useMemo(() => {
    return policies.filter(p => p.policy_type === "legislation" || (p.scope === "international" && p.policy_type === "guideline")).sort((a, b) => a.year_adopted - b.year_adopted);
  }, [policies]);

  if (loading) return <div className="flex items-center justify-center py-32"><div className="w-10 h-10 rounded-full border-2 border-cyan-500/30 border-t-cyan-500 animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="policy-timeline">
      <div>
        <h2 className="text-xl font-black text-white">Policy Timeline</h2>
        <p className="text-xs text-slate-400">Evolution of global AI governance from {cumulativeData[0]?.year || "—"} to {cumulativeData[cumulativeData.length - 1]?.year || "—"}</p>
      </div>

      {/* Cumulative Growth */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Cumulative Policy Growth</h3>
        <div className="h-56">
          <ResponsiveContainer>
            <AreaChart data={cumulativeData}>
              <defs><linearGradient id="ciiGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#06b6d4" stopOpacity={0.3} /><stop offset="100%" stopColor="#06b6d4" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="year" tick={{ fill: "#64748b", fontSize: 10 }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="cumulative" name="Total Policies" stroke="#06b6d4" fill="url(#ciiGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orientation Over Time */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Orientation Over Time</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={orientTimeline}>
                <XAxis dataKey="year" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="care" name="Care" stackId="a" fill={ORIENT_COLORS.care} />
                <Bar dataKey="utility" name="Utility" stackId="a" fill={ORIENT_COLORS.utility} />
                <Bar dataKey="creative" name="Creative" stackId="a" fill={ORIENT_COLORS.creative} />
                <Bar dataKey="extraction" name="Extraction" stackId="a" fill={ORIENT_COLORS.extraction} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-3 mt-2 justify-center">
            {Object.entries(ORIENT_COLORS).map(([o, c]) => (
              <span key={o} className="flex items-center gap-1.5 text-[9px] capitalize"><div className="w-2 h-2 rounded-full" style={{ background: c }} />{o}</span>
            ))}
          </div>
        </div>

        {/* Type Over Time */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Policy Types Over Time</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={typeTimeline}>
                <XAxis dataKey="year" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                {Object.entries(TYPE_COLORS).map(([t, c]) => <Bar key={t} dataKey={t} name={t} stackId="a" fill={c} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Key Milestones */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="milestones">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Key Legislative Milestones</h3>
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-px bg-cyan-500/20" />
          <div className="space-y-4 pl-10">
            {milestones.slice(0, 15).map((p, i) => {
              const cColor = p.policy_cii >= 60 ? "#10b981" : p.policy_cii >= 30 ? "#f59e0b" : "#ef4444";
              return (
                <div key={`${p.policy_name}-${i}`} className="relative animate-fade-in" style={{ animationDelay: `${i * 0.05}s` }}>
                  <div className="absolute -left-[26px] top-1 w-3 h-3 rounded-full border-2 border-cyan-500/40 bg-[#0a0f1a]" />
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold text-white">{p.policy_name}</p>
                      <p className="text-[10px] text-slate-400">{p.country} &middot; {p.year_adopted} &middot; {p.scope}</p>
                    </div>
                    <span className="text-sm font-black flex-shrink-0" style={{ color: cColor }}>{p.policy_cii}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
